// Building blocks shared by the tracker and vehicle pages.
import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from './Links';
import {
  Check, Customer, customers, DataSource, fleet, ModelFamily, NetworkType, Operator, Outcome, TrackerFields, trackers, VehicleFields, VehicleType,
} from '../api';
import { dateTime, TYPE_LABEL } from '../format';
import { Flag } from './Flag';
import { SearchSelect } from './SearchSelect';

export const SOURCES: Record<DataSource, string> = { fms: 'FMS', tacho: 'Tacho direct', lvcan: 'LV CAN', allcan: 'ALL CAN' };
const NETWORKS: NetworkType[] = ['2G', '3G', '4G', 'LTE-M', 'NB-IoT'];
const CHECK_TITLE = { problem: 'Problem', warning: 'Check', info: 'Note' } as const;
export const OUTCOME_LABEL: Record<Outcome, string> = { stock: 'Back to stock', faulty: 'Faulty', retired: 'Retired' };

// Where "← Back" goes: the list (with its filters) we came from, if any.
export interface BackState {
  from?: string;
  fromLabel?: string;
}

export const dash = (v: ReactNode) => (v === null || v === undefined || v === '' ? <span className="muted">–</span> : v);

// Empty boxes mean "not set".
export function blankToNull<T extends object>(f: T): T {
  return Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v])) as T;
}

// A datetime-local value (this device's time) as ISO, or null for "now".
export const localToIso = (v: string) => (v ? new Date(v).toISOString() : null);

// A card that shows facts, with an Edit button that swaps in a form.
export function Panel({ title, editing, setEditing, children, form, actions }: {
  title: string;
  editing: boolean;
  setEditing?: (v: boolean) => void;
  children: ReactNode;
  form?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="card form" aria-label={title}>
      <div className="page-head">
        <h2>{title}</h2>
        {!editing && setEditing && <button className="link" onClick={() => setEditing(true)}>Edit</button>}
        {!editing && actions}
      </div>
      {editing ? form : children}
    </section>
  );
}

export function useSave<T>(save: (f: T) => Promise<unknown>, onSaved: () => void, close: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = (f: T) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await save(f);
      close();
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };
  return { busy, error, submit };
}

export function Diagnostic({ checks, lastReceived, lastFix, operator, recent }: {
  checks: Check[];
  lastReceived: string | null;
  lastFix: string | null;
  operator: Operator | null;
  recent: { points: number; late: number };
}) {
  const problem = checks.some((c) => c.level === 'problem');
  const warning = checks.some((c) => c.level === 'warning');
  return (
    <section className="card form" aria-label="Diagnostic">
      <div className="page-head">
        <h2>Diagnostic</h2>
        <span className={`diag diag-${problem ? 'problem' : warning ? 'warning' : 'ok'}`}>{problem ? 'Problem' : warning ? 'Check' : 'OK'}</span>
      </div>
      {checks.length > 0 && (
        <ul className="checks">
          {checks.map((c) => <li key={c.text} className={`check-${c.level}`}><b>{CHECK_TITLE[c.level]}:</b> {c.text}</li>)}
        </ul>
      )}
      <dl className="facts">
        <dt>Last data</dt><dd>{lastReceived ? dateTime(lastReceived) : '–'}</dd>
        <dt>Last record made</dt><dd>{lastFix ? dateTime(lastFix) : '–'}</dd>
        <dt>GSM operator</dt>
        <dd>{operator ? (
          <><Flag country={operator.country} name={operator.countryName} />{operator.name}
            {operator.countryName && <span className="muted"> · {operator.countryName}</span>}
            <span className="muted small"> ({operator.code})</span></>
        ) : dash(null)}</dd>
        <dt>Last 24 h</dt><dd>{recent.points} records{recent.late ? `, ${recent.late} late` : ''}</dd>
      </dl>
    </section>
  );
}

// --- Tracker details (Device & SIM) ---------------------------------------------------------

type TrackerData = TrackerFields & { id: number; imei: string; inTraccar: boolean; createdAt: string | null };

function trackerForm(t: TrackerData): TrackerFields {
  return {
    model: t.model, serial: t.serial, firmware: t.firmware, networkType: t.networkType, simNumber: t.simNumber,
    simActive: t.simActive, simIccid: t.simIccid, simImsi: t.simImsi, simApn: t.simApn,
    dropIfPowerOff: t.dropIfPowerOff, ignoreGpsAccuracy: t.ignoreGpsAccuracy, notes: t.notes,
  };
}

export function TrackerPanel({ tracker, onSaved }: { tracker: TrackerData; onSaved: () => void }) {
  const t = tracker;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(trackerForm(t));
  const { busy, error, submit } = useSave((x: TrackerFields) => trackers.save(t.id, blankToNull(x)), onSaved, () => setEditing(false));
  const open = (v: boolean) => { setF(trackerForm(tracker)); setEditing(v); };
  const str = (k: keyof TrackerFields) => ({ value: (f[k] as string | null) ?? '', onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });

  const form = (
    <form className="form" onSubmit={submit(f)}>
      <div className="grid2">
        <label>Model<ModelSelect id="d-model" value={f.model} onChange={(m) => setF({ ...f, model: m })} /></label>
        <label>Serial number<input id="d-serial" type="text" maxLength={60} {...str('serial')} /></label>
        <label>Firmware<input id="d-fw" type="text" maxLength={60} {...str('firmware')} /></label>
        <label>Network
          <select id="d-net" value={f.networkType ?? ''} onChange={(e) => setF({ ...f, networkType: (e.target.value || null) as NetworkType | null })}>
            <option value="">–</option>
            {NETWORKS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>SIM phone number<input id="d-simno" type="text" maxLength={30} inputMode="tel" {...str('simNumber')} /></label>
        <label>SIM status
          <select id="d-simst" value={f.simActive === null ? '' : f.simActive ? 'active' : 'inactive'}
            onChange={(e) => setF({ ...f, simActive: e.target.value === '' ? null : e.target.value === 'active' })}>
            <option value="">–</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
        </label>
        <label>ICCID<input id="d-iccid" type="text" maxLength={30} inputMode="numeric" {...str('simIccid')} /></label>
        <label>IMSI<input id="d-imsi" type="text" maxLength={20} inputMode="numeric" {...str('simImsi')} /></label>
        <label>APN <span className="muted small">(name only, no password)</span><input id="d-apn" type="text" maxLength={100} {...str('simApn')} /></label>
      </div>
      <label className="check"><input id="d-drop" type="checkbox" checked={f.dropIfPowerOff} onChange={(e) => setF({ ...f, dropIfPowerOff: e.target.checked })} /> Drop data if power off <span className="muted small">(info only)</span></label>
      <label className="check"><input id="d-acc" type="checkbox" checked={f.ignoreGpsAccuracy} onChange={(e) => setF({ ...f, ignoreGpsAccuracy: e.target.checked })} /> Ignore GPS accuracy <span className="muted small">(info only)</span></label>
      <label>Notes<textarea id="d-notes" rows={3} maxLength={5000} {...str('notes')} /></label>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button className="primary" disabled={busy}>Save</button>
        <button type="button" className="link" onClick={() => open(false)}>Cancel</button>
      </div>
    </form>
  );

  return (
    <Panel title="Device & SIM" editing={editing} setEditing={open} form={form}>
      <dl className="facts">
        <dt>Model</dt><dd>{dash(t.model)}</dd>
        <dt>IMEI</dt><dd className="mono">{t.imei}</dd>
        <dt>Serial</dt><dd>{dash(t.serial)}</dd>
        <dt>Firmware</dt><dd>{dash(t.firmware)}</dd>
        <dt>Network</dt><dd>{dash(t.networkType)}</dd>
        <dt>Added</dt><dd>{t.createdAt ? dateTime(t.createdAt).replace(/,.*/, '') : '–'}</dd>
        <dt>SIM number</dt><dd>{dash(t.simNumber)}</dd>
        <dt>SIM status</dt><dd>{t.simActive === null ? dash(null) : t.simActive ? 'Active' : 'Inactive'}</dd>
        <dt>ICCID</dt><dd className="mono">{dash(t.simIccid)}</dd>
        <dt>IMSI</dt><dd className="mono">{dash(t.simImsi)}</dd>
        <dt>APN</dt><dd>{dash(t.simApn)}</dd>
        <dt>Drop if power off</dt><dd>{t.dropIfPowerOff ? 'Yes' : 'No'}</dd>
        <dt>Ignore GPS accuracy</dt><dd>{t.ignoreGpsAccuracy ? 'Yes' : 'No'}</dd>
        <dt>In Traccar</dt><dd>{t.inTraccar ? 'Yes' : 'No'}</dd>
      </dl>
      {t.notes && <p className="prewrap">{t.notes}</p>}
    </Panel>
  );
}

// --- Vehicle details ------------------------------------------------------------------------

type VehicleData = VehicleFields & { id: number; customer: { id: number; name: string } | null };

function vehicleForm(v: VehicleData): VehicleFields {
  return {
    name: v.name, registration: v.registration, type: v.type, customerId: v.customerId, make: v.make, model: v.model,
    vin: v.vin, year: v.year, voltageSystem: v.voltageSystem, maxSpeedKmh: v.maxSpeedKmh, dataSources: v.dataSources,
  };
}

// The customer dropdown, loading the list on first show.
// Teltonika models, by family. The model decides how the tracker's IO values are named.
let modelList: Promise<ModelFamily[]> | null = null;
export function ModelSelect({ id, value, onChange, emptyLabel = '– not set –' }: {
  id: string; value: string | null; onChange: (v: string | null) => void; emptyLabel?: string;
}) {
  const [families, setFamilies] = useState<ModelFamily[] | null>(null);
  useEffect(() => {
    modelList ??= trackers.models().then((r) => r.families);
    modelList.then(setFamilies).catch(() => { modelList = null; });
  }, []);
  const known = families?.some((f) => f.models.includes(value ?? ''));
  return (
    <SearchSelect id={id} value={value ?? ''} onChange={(v) => onChange(v || null)} emptyLabel={emptyLabel}
      options={[
        ...(value && !known ? [{ value, label: value }] : []),
        ...(families ?? []).flatMap((f) => f.models.map((m) => ({ value: m, label: m, group: f.label }))),
      ]} />
  );
}

export function CustomerSelect({ id, value, current, onChange }: {
  id: string;
  value: number | null;
  current?: { id: number; name: string } | null;
  onChange: (v: number | null) => void;
}) {
  const [list, setList] = useState<Customer[] | null>(null);
  useEffect(() => {
    customers.list().then((r) => setList(r.customers)).catch(() => {});
  }, []);
  return (
    <SearchSelect id={id} value={value === null ? '' : String(value)} onChange={(v) => onChange(v ? Number(v) : null)} emptyLabel="– none –"
      options={[
        // Keep the current customer selectable while the list loads.
        ...(current && !list?.some((c) => c.id === current.id) ? [{ value: String(current.id), label: current.name }] : []),
        ...(list ?? []).map((c) => ({ value: String(c.id), label: c.name })),
      ]} />
  );
}

export function VehiclePanel({ vehicle, onSaved }: { vehicle: VehicleData; onSaved: () => void }) {
  const v = vehicle;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(vehicleForm(v));
  const { busy, error, submit } = useSave((x: VehicleFields) => fleet.saveVehicle(v.id, blankToNull(x)), onSaved, () => setEditing(false));
  const open = (on: boolean) => { setF(vehicleForm(vehicle)); setEditing(on); };
  const str = (k: 'name' | 'registration' | 'make' | 'model' | 'vin') =>
    ({ value: f[k] ?? '', onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  const toggle = (s: DataSource, on: boolean) =>
    setF({ ...f, dataSources: on ? [...f.dataSources, s] : f.dataSources.filter((x) => x !== s) });

  const form = (
    <form className="form" onSubmit={submit(f)}>
      <div className="grid2">
        <label>Name<input id="v-name" type="text" required maxLength={60} {...str('name')} /></label>
        <label>Registration<input id="v-reg" type="text" maxLength={30} {...str('registration')} /></label>
        <label>Customer<CustomerSelect id="v-cust" value={f.customerId} current={v.customer} onChange={(c) => setF({ ...f, customerId: c })} /></label>
        <label>Type
          <select id="v-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value as VehicleType })}>
            {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label>Make<input id="v-make" type="text" maxLength={60} {...str('make')} /></label>
        <label>Model<input id="v-model" type="text" maxLength={60} {...str('model')} /></label>
        <label>VIN<input id="v-vin" type="text" maxLength={30} className="mono" {...str('vin')} /></label>
        <label>Year<input id="v-year" type="number" min={1950} max={2100} value={f.year ?? ''} onChange={(e) => setF({ ...f, year: e.target.value ? Number(e.target.value) : null })} /></label>
        <label>Voltage system
          <select id="v-volt" value={f.voltageSystem ?? ''} onChange={(e) => setF({ ...f, voltageSystem: e.target.value ? (Number(e.target.value) as 12 | 24) : null })}>
            <option value="">–</option><option value="12">12 V</option><option value="24">24 V</option>
          </select>
        </label>
        <label>Max-speed filter (km/h)<input id="v-maxspd" type="number" min={30} max={400} required value={f.maxSpeedKmh} onChange={(e) => setF({ ...f, maxSpeedKmh: Number(e.target.value) })} /></label>
      </div>
      <fieldset>
        <legend className="small">Data sources fitted</legend>
        <div className="checks-row">
          {Object.entries(SOURCES).map(([k, l]) => (
            <label key={k} className="check"><input type="checkbox" checked={f.dataSources.includes(k as DataSource)} onChange={(e) => toggle(k as DataSource, e.target.checked)} /> {l}</label>
          ))}
        </div>
      </fieldset>
      <p className="muted small">Points faster than the max-speed filter are treated as GPS errors; changing it recalculates all journeys. <Link to="/customers">Add a customer</Link></p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button className="primary" disabled={busy}>Save</button>
        <button type="button" className="link" onClick={() => open(false)}>Cancel</button>
      </div>
    </form>
  );

  return (
    <Panel title="Vehicle" editing={editing} setEditing={open} form={form}>
      <dl className="facts">
        <dt>Customer</dt><dd>{v.customer ? <Link to={`/customers/${v.customer.id}`}>{v.customer.name}</Link> : dash(null)}</dd>
        <dt>Registration</dt><dd>{dash(v.registration)}</dd>
        <dt>Type</dt><dd>{TYPE_LABEL[v.type]}</dd>
        <dt>Make / model</dt><dd>{dash([v.make, v.model].filter(Boolean).join(' '))}</dd>
        <dt>VIN</dt><dd className="mono">{dash(v.vin)}</dd>
        <dt>Year</dt><dd>{dash(v.year)}</dd>
        <dt>Voltage</dt><dd>{v.voltageSystem ? `${v.voltageSystem} V` : dash(null)}</dd>
        <dt>Max-speed filter</dt><dd>{v.maxSpeedKmh} km/h</dd>
        <dt>Data sources</dt><dd>{v.dataSources.length ? v.dataSources.map((s) => SOURCES[s]).join(', ') : 'None'}</dd>
      </dl>
    </Panel>
  );
}

// --- Removing a tracker from its vehicle ----------------------------------------------------

export function RemoveForm({ installationId, imei, vehicleName, onDone, onCancel }: {
  installationId: number;
  imei: string;
  vehicleName: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [when, setWhen] = useState('');
  const [state, setState] = useState<Outcome>('stock');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await fleet.remove(installationId, { removedAt: localToIso(when), trackerState: state, notes: notes.trim() || null });
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <form className="form callout" onSubmit={submit}>
      <strong>Remove tracker <span className="mono">{imei}</span> from {vehicleName}</strong>
      <div className="grid2">
        <label>Removed at <span className="muted small">(empty = now)</span><input id="rm-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
        <label>Tracker goes to
          <select id="rm-state" value={state} onChange={(e) => setState(e.target.value as Outcome)}>
            {Object.entries(OUTCOME_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>
      <label>Note <span className="muted small">(optional)</span><input id="rm-notes" type="text" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      <p className="muted small">Journeys after this time no longer count for {vehicleName}. Set the tracker's second server back if it's leaving Fleet Functions.</p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="actions">
        <button className="danger-button" disabled={busy}>Remove tracker</button>
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
